import { useCallback, useEffect, useRef, useState } from "react";
import ColumnsTable from "./components/ColumnsTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getPaginatedDriverDataApi,
  updateDriverStatusApi,
  deleteDriverHandleApi,
  searchDriversApi,
} from "../../../services/customAPI";
import ReactPaginate from "react-paginate";
import Loader from "components/loader/loader";
import { useDisclosure } from "@chakra-ui/hooks";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/modal";
import { Button, ChakraProvider } from "@chakra-ui/react";
import deleteIcon from "../../../assets/svg/deleteIcon.svg";
import blockIcon from "../../../assets/svg/blockIcon.svg";
import "./driverlist.css";
import Navbar from "../../../components/navbar";
import { t } from "i18next";
import { DTO, Types } from "shared-lib";
import { errorToast, successToast } from "utils/commons";
import { DriverTableData } from "../../../types/driver-table.types";
import {
  PageClickEvent,
  ApiError,
  SearchDriverResponse,
  PaginatedDriverResponse,
} from "../../../types/drivers-view.types";
import Card from "components/card";

const Drivers = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const currentPage = useRef<number>(1);
  const navigate = useNavigate();
  const limit = 10;
  const [selectedItem, setSelectedItem] = useState<DriverTableData | null>(null);
  const [viewItem, setViewItem] = useState<DriverTableData | null>(null);
  const [modalState, setModalState] = useState<boolean>(true);
  const [pageCount, setPageCount] = useState<number>(1);
  const [searchText, setSearchText] = useState<string>("");
  const [driverData, setDriverData] = useState<DriverTableData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [pageItemStartNumber, setPageItemStartNumber] = useState<string>("0");
  const [pageItemEndNumber, setPageItemEndNumber] = useState<string>("0");

  const [searchParams, setSearchParams] = useSearchParams();

  const getFilterFromUrl = (): Types.DriverStatusEnum | string => {
    const data = searchParams.get("status");
    if (data === Types.DriverStatusEnum.ACTIVE || data === "active") return Types.DriverStatusEnum.ACTIVE;
    if (data === Types.DriverStatusEnum.INACTIVE || data === "inactive") return Types.DriverStatusEnum.INACTIVE;
    return "ALL";
  };

  const [driverStatus, setDriverStatus] = useState<Types.DriverStatusEnum | string>(getFilterFromUrl());

  const updateUrlParams = useCallback((status: Types.DriverStatusEnum | string, page: number) => {
    const params = new URLSearchParams(searchParams);
    if (status && status !== "ALL") {
      params.set("status", status);
    } else {
      params.delete("status");
    }
    if (page > 1) {
      params.set("page", String(page));
    } else {
      params.delete("page");
    }
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleStatusChange = useCallback((status: Types.DriverStatusEnum | string) => {
    setDriverStatus(status);
    currentPage.current = 1;
    updateUrlParams(status, 1);
  }, [updateUrlParams]);

  const firstRender = useRef<boolean>(true);
  const setPageItemRange = useCallback((currPageNumber: number, maxItemRange: number) => {
    let startNumber = currPageNumber * limit - limit + 1;
    if (startNumber < 0) {
      startNumber = 0;
    }
    setPageItemStartNumber(
      startNumber.toLocaleString("en-US", {
        minimumIntegerDigits: 2,
        useGrouping: false,
      })
    );

    const endNumber = currPageNumber * limit;
    setPageItemEndNumber(
      Math.min(endNumber, maxItemRange).toLocaleString("en-US", {
        minimumIntegerDigits: 2,
        useGrouping: false,
      })
    );
  }, [limit]);

  const deleteHandle = async (info: DriverTableData['action']) => {
    setLoading(true);
    setErrorMessage("");
    onClose();
    try {
      const response = await deleteDriverHandleApi(Number(info.id));
      if (response) {
        successToast(t("Motorista desativado com sucesso"));
        if (driverData.length % limit === 1) {
          currentPage.current = currentPage.current - 1;
        }
        getPaginatedDriverData();
      } else {
        errorToast(t("Alguma coisa deu errado!"));
      }
      setLoading(false);
    } catch (error) {
      errorToast(t("Alguma coisa deu errado!"));
      setErrorMessage(t("Erro ao desativar motorista"));
      console.error("error :>> ", error);
      setLoading(false);
    }
  };

  const searchDrivers = async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const response: SearchDriverResponse = await searchDriversApi({
        page: currentPage.current,
        limit: limit,
        query: searchText.trim(),
        status: driverStatus !== "ALL" ? driverStatus : undefined,
      });

      return response;
    } catch (error: unknown) {
      const apiError = error as ApiError;
      setDriverData([]);
      setPageCount(1);
      setErrorMessage(apiError.message || t("Erro ao buscar motoristas"));
      console.error(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const searchDriverFunction = async () => {
    try {
      setLoading(true);
      const response = await searchDrivers();
      if (!response) {
        return;
      }
      setPageCount(Math.ceil(response.total / limit));
      setPageItemRange(currentPage.current, response.total);
    } catch (error) {
      console.error("search driver error:", error);
      setErrorMessage(t("Erro ao buscar motoristas"));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = async (e: React.SyntheticEvent<any>) => {
    e.preventDefault();
    if (searchText.trim() === "") {
      return;
    }
    currentPage.current = 1;
    searchDriverFunction();
  };

  function handlePageClick(e: PageClickEvent) {
    currentPage.current = e.selected + 1;
    if (searchText !== "") {
      searchDriverFunction();
    } else {
      getPaginatedDriverData();
    }
  }

  function convertToDriverArray(driverArray: Array<DTO.DriverDTO>) {
    if (!driverArray || !Array.isArray(driverArray)) {
      return [];
    }
    const BASE_IMAGE_URL = `${process.env.REACT_APP_BASE_URL}/uploads/`;
    return driverArray.map((driver: DTO.DriverDTO, index) => ({
      id: driver.id.toString(),
      key: { index },
      fullName: {
        name: driver.firstName + " " + driver.lastName,
        path: driver.profileImageKey
          ? `${BASE_IMAGE_URL}${driver.profileImageKey}`
          : "",
      },
      mobileNumber: driver.mobileNumber,
      vehicleNumber: driver?.vehicle ? driver.vehicle.vehicleNumber : "N/A",
      vehicleId: driver?.vehicle?.id ?? null,
      vehicleImageUrl: driver?.vehicle?.profileImageKey
        ? `${BASE_IMAGE_URL}${driver.vehicle.profileImageKey}`
        : "",
      vehicleType: driver?.vehicle ? driver.vehicle?.vehicleCategory : null,
      status: driver.status,
      action: {
        driverStatus: driver.status,
        id: driver.id.toString(),
        approved: (driver.approved as Types.ApprovalStatus) ?? Types.ApprovalStatus.PENDING,
        documentsKey: Array.isArray(driver?.documentsKey) ? driver.documentsKey : (driver?.documentsKey ? [driver.documentsKey] : []),
        profileImageKey: driver?.profileImageKey ? driver?.profileImageKey : "",
      },
      rawInfo: driver,
    }));
  }

  const getPaginatedDriverData = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const response: PaginatedDriverResponse = await getPaginatedDriverDataApi({
        page: currentPage.current,
        limit: limit,
        status: driverStatus !== "ALL" ? driverStatus : undefined,
      });
      const totalDrivers = response?.totalDrivers || 0;
      setPageCount(Math.max(1, Math.ceil(totalDrivers / limit)));
      setPageItemRange(currentPage.current, totalDrivers);
      const convertedData = convertToDriverArray(response?.data);
      setDriverData(convertedData);
    } catch (error) {
      console.error("Error fetching data:", error);
      errorToast(t("Alguma coisa deu errado!"));
      setErrorMessage(t("Erro ao carregar motoristas"));
    } finally {
      setLoading(false);
    }
  }, [limit, setPageItemRange]);

  const updateDriverStatus = async (id: string, status: Types.DriverStatusEnum) => {
    setLoading(true);
    setErrorMessage("");
    try {
      onClose();
      const result = await updateDriverStatusApi(Number(id), { status });

      // @ts-ignore
      if (result?.message || result?.data?.message) {
        successToast(t("Status atualizado com sucesso"));
        getPaginatedDriverData();
        navigate("/admin/drivers");
      } else {
        errorToast(t("Alguma coisa deu errado!"));
      }
      setLoading(false);
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error("error :>> ", apiError?.message || error);
      errorToast(t("Alguma coisa deu errado!"));
      setErrorMessage(apiError.message || t("Erro ao atualizar status"));
      setLoading(false);
    }
  };

  const handleClickForDeleteModal = (data: DriverTableData) => {
    setLoading(true);
    setModalState(true);
    setSelectedItem(data);
    onOpen();
    setLoading(false);
  };

  const handleToggleForStatusMOdal = (data: DriverTableData) => {
    setLoading(true);
    setModalState(false);
    setSelectedItem(data);
    onOpen();
    setLoading(false);
  };

  const handleViewDriver = (data: DriverTableData) => {
    setViewItem(data);
    onViewOpen();
  };

  useEffect(() => {
    currentPage.current = 1;
    // Only call this on mount and when driverStatus changes
    getPaginatedDriverData();
  }, [getPaginatedDriverData, driverStatus]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
    } else if (searchText.trim() === "") {
      currentPage.current = 1;
      updateUrlParams(driverStatus, 1);
      getPaginatedDriverData();
    }
  }, [getPaginatedDriverData, searchText, driverStatus, updateUrlParams]);

  return (
    <div>
      <Navbar
        flag={true}
        brandText={t("Drivers")}
        handleSearch={(e: React.SyntheticEvent<EventTarget>) =>
          handleSearchSubmit(e)
        }
        setSearchText={setSearchText}
      />
      {errorMessage ? (
        <Card extra="w-full mt-4 p-4 h-full">
          <div className="text-sm text-red-400">{errorMessage}</div>
        </Card>
      ) : null}
      {loading ? (
        <Loader />
      ) : (
        <>
          <>
            <div className="mt-4">
              <ColumnsTable
                tableData={driverData}
                handleClickForDeleteModal={handleClickForDeleteModal}
                handleToggleForStatusMOdal={handleToggleForStatusMOdal}
                handleViewDriver={handleViewDriver}
                statusOptions={[
                  { value: "ALL", label: t("All") },
                  { value: Types.DriverStatusEnum.ACTIVE, label: t("Active") },
                  { value: Types.DriverStatusEnum.INACTIVE, label: t("Inactive") },
                ]}
                setDriverStatus={handleStatusChange}
                driverStatus={driverStatus}
              />

              <div
                className="mx-2 "
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  color: "#5E6278",
                }}
              >
                <h5>
                  {pageItemStartNumber} - {pageItemEndNumber}
                </h5>
                <div style={{ marginTop: "1rem" }}>
                  <ReactPaginate
                    breakLabel=" .  .  . "
                    nextLabel=">"
                    onPageChange={handlePageClick}
                    pageRangeDisplayed={3}
                    pageCount={pageCount}
                    previousLabel="<"
                    renderOnZeroPageCount={null}
                    marginPagesDisplayed={2}
                    containerClassName="pagination"
                    pageClassName="page-item"
                    pageLinkClassName="page-link"
                    previousClassName="previous-page-btn"
                    previousLinkClassName="page-link"
                    nextClassName="next-page-btn"
                    nextLinkClassName="page-link"
                    activeClassName="active"
                    forcePage={currentPage.current - 1}
                  />
                </div>
              </div>
            </div>
          </>
          <>
            {isOpen && (
              <ChakraProvider>
                <Modal
                  isCentered={true}
                  isOpen={isOpen}
                  onClose={onClose}
                  size="xs"
                >
                  <ModalOverlay />
                  <ModalContent>
                    <ModalHeader></ModalHeader>
                    {/* <ModalCloseButton /> */}
                    <div className="mb-2 flex justify-center">
                      {modalState ? (
                        <img src={deleteIcon} alt={t("Excluir")} />
                      ) : (
                        <img src={blockIcon} alt={t("Bloquear")} />
                      )}
                    </div>
                    {modalState ? (
                      <ModalBody className="text-center">
                        {t("Are you sure you want to Disable?")} <br />
                        {'"' + selectedItem.fullName.name + '"'}
                      </ModalBody>
                    ) : (
                      <ModalBody className="text-center">
                        {selectedItem.action.driverStatus === "active"
                          ? "Are you sure you want to Unassign ?"
                          : "Are you sure you want to Assign ?"}
                        <br />
                        {'"' + selectedItem.fullName.name + '"'}
                      </ModalBody>
                    )}

                    <div className="mt-3 flex justify-center">
                      {modalState ? (
                        <Button
                          // style={{backgroundColor: 'red'}}
                          className="cancel-delete-modal-button mx-2"
                          onClick={onClose}
                        >
                          {t("cancel")}
                        </Button>
                      ) : (
                        <Button
                          className="cancel-block-modal-button mx-2"
                          onClick={onClose}
                        >
                          {t("cancel")}
                        </Button>
                      )}
                      {modalState ? (
                        <Button
                          className="delete-modal-button mx-2"
                          onClick={() => deleteHandle(selectedItem?.action)}
                        >
                          {selectedItem.action.driverStatus === "inactive"
                            ? t("enable")
                            : t("disable")}
                        </Button>
                      ) : (
                        <Button
                          className="block-modal-button mx-2"
                          onClick={() => {
                            const nextStatus =
                              selectedItem.action.driverStatus === Types.DriverStatusEnum.ACTIVE
                                ? Types.DriverStatusEnum.INACTIVE
                                : Types.DriverStatusEnum.ACTIVE;
                            updateDriverStatus(selectedItem.action.id, nextStatus);
                          }}
                        >
                          {selectedItem.action.driverStatus === "active"
                            ? "Unassign"
                            : "Assign"}
                        </Button>
                      )}
                    </div>
                    <ModalFooter></ModalFooter>
                  </ModalContent>
                </Modal>
              </ChakraProvider>
            )}
          </>

          {/* View Driver Modal */}
          {isViewOpen && viewItem && viewItem.rawInfo && (
            <ChakraProvider>
              <Modal isCentered isOpen={isViewOpen} onClose={onViewClose} size="md">
                <ModalOverlay />
                <ModalContent style={{ background: "#1B254B", color: "#fff" }}>
                  <ModalHeader style={{ textAlign: "center", borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: "12px" }}>
                    {t("Detalhes do Motorista")}
                  </ModalHeader>
                  <ModalBody>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "16px", padding: "16px 0" }}>
                      {/* Profile Image */}
                      {viewItem.rawInfo.profileImageKey ? (
                        <img
                          src={`${process.env.REACT_APP_BASE_URL}/uploads/${viewItem.rawInfo.profileImageKey}`}
                          alt="Profile"
                          style={{ width: "80px", height: "80px", borderRadius: "50%", objectFit: "cover", border: "3px solid rgba(255,255,255,0.2)" }}
                        />
                      ) : (
                        <div style={{ width: "80px", height: "80px", borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "32px", color: "#a0aec0" }}>
                          {viewItem.rawInfo.firstName?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                      )}

                      {/* Info Grid */}
                      <div style={{ width: "100%", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                        <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: "8px", padding: "12px" }}>
                          <p style={{ fontSize: "11px", color: "#a0aec0", marginBottom: "4px" }}>{t("Name")}</p>
                          <p style={{ fontSize: "14px", fontWeight: 600 }}>{viewItem.rawInfo.firstName || "N/D"} {viewItem.rawInfo.lastName || ""}</p>
                        </div>
                        <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: "8px", padding: "12px" }}>
                          <p style={{ fontSize: "11px", color: "#a0aec0", marginBottom: "4px" }}>{t("Mobile Number")}</p>
                          <p style={{ fontSize: "14px", fontWeight: 600 }}>{viewItem.rawInfo.mobileNumber || "N/D"}</p>
                        </div>
                        <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: "8px", padding: "12px" }}>
                          <p style={{ fontSize: "11px", color: "#a0aec0", marginBottom: "4px" }}>{t("Email")}</p>
                          <p style={{ fontSize: "14px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>{viewItem.rawInfo.email || "N/D"}</p>
                        </div>
                        <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: "8px", padding: "12px" }}>
                          <p style={{ fontSize: "11px", color: "#a0aec0", marginBottom: "4px" }}>{t("Status")}</p>
                          <p style={{ fontSize: "14px", fontWeight: 600, color: viewItem.rawInfo.status === Types.DriverStatusEnum.ACTIVE ? "#48bb78" : "#fc5185" }}>
                            {viewItem.rawInfo.status === Types.DriverStatusEnum.ACTIVE ? t("Active") : t("Inactive")}
                          </p>
                        </div>
                        <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: "8px", padding: "12px" }}>
                          <p style={{ fontSize: "11px", color: "#a0aec0", marginBottom: "4px" }}>{t("Total Rides")}</p>
                          <p style={{ fontSize: "14px", fontWeight: 600 }}>{viewItem.rawInfo.totalRidesCompleted ?? 0}</p>
                        </div>
                        <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: "8px", padding: "12px" }}>
                          <p style={{ fontSize: "11px", color: "#a0aec0", marginBottom: "4px" }}>{t("Average Rating")}</p>
                          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <p style={{ fontSize: "14px", fontWeight: 600, color: "#ecc94b" }}>{viewItem.rawInfo.averageRating ? Number(viewItem.rawInfo.averageRating).toFixed(1) : "N/D"}</p>
                            <span style={{ color: "#ecc94b", fontSize: "14px" }}>★</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </ModalBody>
                  <ModalFooter style={{ borderTop: "1px solid rgba(255,255,255,0.1)", justifyContent: "center" }}>
                    <Button className="cancel-block-modal-button" onClick={onViewClose}>
                      {t("Fechar")}
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
            </ChakraProvider>
          )}

        </>
      )}
    </div>
  );
};

export default Drivers;
